import React from 'react';
import { useAdSummaryMetrics } from '../hooks/useAdSummaryMetrics';
import { TopicAdSummaryMetrics } from '../hooks/types';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useHistory,
  useMainTimePeriodShortDisplayName,
  useMetricFormatter,
  useQueryParamValue
} from 'src/utils/Hooks';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import { calculatePercentChange, updateUrlQueryParams } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { paidSearchMetrics, PaidSearchTopicCardDef } from './constants';
import { getAdjustedDataSetsForSpline, getSplineTotals, saveSplineAsDelimitedData } from 'src/utils/chartUtils';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useCompanyName } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const paidSearchMetricMap = paidSearchMetrics.reduce((acc, metricInfo) => {
  return {
    ...acc,
    [metricInfo.fieldId]: metricInfo
  };
}, {} as Record<string, PaidSearchTopicCardDef>);

const titleOverrides = {
  adImpressions: 'Impressions',
  conversionRate: 'Conversion Rate'
};

const dropdownSortOrder = [
  'adClicks',
  'adImpressions',
  'ctr',
  'cpc',
  'cpm',
  'cpa',
  'adSales',
  'conversionRate',
  'adUnitsSold',
  'roas',
  'keywordCount'
];

/**
 * Shows spline chart for paid search topics
 */
export const PaidSearchTopicsMultiMetricSplineChart = () => {
  const formatMetric = useMetricFormatter();
  const { displayName: mainPeriodName } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonPeriodName } = useAppSelector((state) => state.comparisonTimePeriod);
  const { getComparisonSplineData, getPrimarySplineData, isLoading } = useAdSummaryMetrics();
  const selectedMetric = useQueryParamValue('adMetric', 'adSpend') as keyof TopicAdSummaryMetrics;
  const history = useHistory();

  const rawPrimaryData = getPrimarySplineData(selectedMetric);
  const rawComparisonData = getComparisonSplineData(selectedMetric);

  const { primaryData, secondaryData } = getAdjustedDataSetsForSpline(rawPrimaryData, rawComparisonData);
  const { primaryTotal, secondaryTotal } = getSplineTotals(rawPrimaryData, rawComparisonData, {
    func: paidSearchMetricMap[selectedMetric]?.func || 'sum'
  });
  const percentChange = calculatePercentChange(primaryTotal, secondaryTotal);
  const metricType = paidSearchMetricMap[selectedMetric]?.metricType;

  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const companyName = useCompanyName();
  const taxonomyText = useTaxonomySubtitleText();

  const getSubtitle = () => {
    const metric = paidSearchMetrics.find((m) => m.fieldId === selectedMetric)?.title ?? '';
    const metricName = ['ROAS', 'CPA', 'CPM', 'CPC', 'CTR %'].includes(metric) ? metric : metric.toLowerCase();

    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the ${metricName} for ${companyName}`}
          {taxonomyText}
          {` in ${entityPhrase}`}
          {` during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <BeaconChartWithLegend
      subtitle={getSubtitle()}
      loading={isLoading}
      includeSummaryInfo={false}
      singleDropdownProps={{
        options: paidSearchMetrics
          .map((metric) => ({
            id: metric.fieldId,
            label: titleOverrides[metric.fieldId] || metric.title
          }))
          .sort((a, b) => dropdownSortOrder.indexOf(a.id) - dropdownSortOrder.indexOf(b.id)),
        selectedId: selectedMetric,
        onChange: (option) => {
          history.push(updateUrlQueryParams({ adMetric: option.id }));
        }
      }}
      primaryLegendProps={{
        displayName: mainPeriodName,
        metric: formatMetric(primaryTotal, metricType, {
          showFullValue: metricType === METRICTYPE.PERCENT,
          decimalPlaces: 2
        }),
        change: formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT),
        direction: getDirection(percentChange)
      }}
      comparisonLegendProps={{
        displayName: comparisonPeriodName,
        metric: formatMetric(secondaryTotal, metricType, {
          showFullValue: metricType === METRICTYPE.PERCENT,
          decimalPlaces: 2
        })
      }}
      convertSeriesToDelimitedData={async () =>
        saveSplineAsDelimitedData(
          primaryData,
          secondaryData,
          `Paid Search - ${titleOverrides[selectedMetric] || paidSearchMetricMap[selectedMetric]?.title}`
        )
      }
    >
      <SplineChart metricType={metricType} primaryData={primaryData} secondaryData={secondaryData} />
    </BeaconChartWithLegend>
  );
};
